import type { NightscoutSource } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Label,
  InfoButton,
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabel,
  SliderValueLabelContainer,
} from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"

import styles from "./DataSource.module.scss"
import { usePolyglot } from "src/contexts"
import {
  useNightscoutSource,
  useSliderInputProps,
  useUpdateSource,
  useDebouncedUpdater,
} from "src/hooks"

const min = 0
const max = 120
const sliderStep = 5
const inputStep = 1
const polyglotPrefix = "pages.settings.data_source.nightscout_delay."

export function NightscoutDelay() {
  const polyglot = usePolyglot()

  const nightscoutSource = useNightscoutSource()
  const sourceId = nightscoutSource?.id

  const updateSource = useUpdateSource()
  const debouncedUpdateSource = useDebouncedUpdater({ updater: updateSource })

  const updateSourceDelay = useCallback(
    (sourceDelay: NightscoutSource["delay"]) => {
      // TODO error toast and try/catch
      if (sourceId) {
        debouncedUpdateSource({
          id: sourceId,
          delay: sourceDelay,
        })
      }
    },
    [
      sourceId,
      debouncedUpdateSource,
    ],
  )

  const {
    inputValue,
    sliderValue,
    onSliderChange,
    onNumberChange,
    onNumberBlur,
    onNumberKeyDown,
  } = useSliderInputProps({
    value: nightscoutSource?.delay ?? 0,
    updatedAt: nightscoutSource?.updated_at,
    onChange: updateSourceDelay,
    min,
    max,
    inputStep,
    sliderStep,
  })

  if (!nightscoutSource) {
    return null
  }

  return (
    <div>
      <Label>
        {polyglot.t(`${polyglotPrefix}label`)}
        <InfoButton title={polyglot.t(`${polyglotPrefix}title`)}>
          {polyglot.t(`${polyglotPrefix}description`)}
        </InfoButton>
      </Label>
      <div className={styles.rangeInputContainer}>
        <SliderValue
          sizePreset="small"
          alignmentPreset="start"
        >
          <SliderValueInput
            min={min}
            max={max}
            step={inputStep}
            value={inputValue}
            onChange={onNumberChange}
            onBlur={onNumberBlur}
            onKeyDown={onNumberKeyDown}
            placeholder=""
            id="nightscout-delay-number"
          />
          <SliderValueLabelContainer>
            <SliderValueLabel>
              {polyglot.t(
                "time.seconds_abbreviated",
                Number(inputValue),
              )}
            </SliderValueLabel>
          </SliderValueLabelContainer>
        </SliderValue>
        <Slider className={styles.rangeInputSlider}>
          <SliderInput
            value={sliderValue}
            min={min}
            max={max}
            step={sliderStep}
            onChange={onSliderChange}
            id="nightscout-delay-slider"
          />
          <SliderTracks
            min={min}
            max={max}
          />
        </Slider>
      </div>
    </div>
  )
}
